import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Plus, CalendarClock, ChevronLeft, Info } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import ShiftTypeModal from '../../components/ShiftTypeModal';
import ConfirmDeleteShiftTypeModal from '../../components/ConfirmDeleteShiftTypeModal';
import apiClient from '../../api/axios';

function formatTime12(time) {
  if (!time) return '';
  const [h, m] = time.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const hour = h % 12 === 0 ? 12 : h % 12;
  return `${hour}:${String(m).padStart(2, '0')} ${period}`;
}

function durationMins(startTime, endTime) {
  const [sh, sm] = startTime.split(':').map(Number);
  const [eh, em] = endTime.split(':').map(Number);
  let mins = (eh * 60 + em) - (sh * 60 + sm);
  if (mins <= 0) mins += 24 * 60;
  return mins;
}

function formatDuration(mins) {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

function ShiftTypes() {
  const { t } = useTranslation('shiftTypes');
  const navigate = useNavigate();
  const [shiftTypes, setShiftTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingShiftType, setEditingShiftType] = useState(null);
  const [deletingShiftType, setDeletingShiftType] = useState(null);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(timer);
  }, [toast]);

  const fetchShiftTypes = useCallback(() => {
    setLoading(true);
    apiClient.get('/api/shift-templates')
      .then(res => {
        const data = Array.isArray(res.data) ? res.data : [];
        data.sort((a, b) => (a.displayOrder ?? 999) - (b.displayOrder ?? 999));
        setShiftTypes(data);
      })
      .catch(() => setShiftTypes([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchShiftTypes(); }, [fetchShiftTypes]);

  const openAdd = () => { setEditingShiftType(null); setModalOpen(true); };
  const openEdit = (st) => { setEditingShiftType(st); setModalOpen(true); };

  const handleSaved = (saved) => {
    const label = saved.label || saved.name || saved.shiftName || '';
    setToast(editingShiftType
      ? t('toast.updated', { label })
      : t('toast.created', { label })
    );
    fetchShiftTypes();
  };

  const handleDeleted = () => {
    setToast(t('toast.deleted'));
    fetchShiftTypes();
  };

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      {toast && (
        <div className="fixed top-4 right-4 z-[60] flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700 shadow-lg max-w-xs">
          <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
          <span className="flex-1">{toast}</span>
          <button onClick={() => setToast(null)} className="text-current opacity-60 hover:opacity-100 focus:outline-none">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      <PageHeader
        title={t('title')}
        subtitle={t('subtitle')}
        actions={[
          <button
            key="back"
            onClick={() => navigate('/settings')}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100 transition"
          >
            <ChevronLeft size={15} />
            Settings
          </button>,
          <button
            key="add"
            onClick={openAdd}
            className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-orange-500 to-amber-600 px-4 py-2 text-sm font-semibold text-white shadow-md shadow-orange-200 transition hover:from-orange-600 hover:to-amber-700 focus:outline-none focus:ring-2 focus:ring-orange-300 focus:ring-offset-2"
          >
            <Plus size={16} />
            {t('addButton')}
          </button>,
        ]}
      />

      <div className="flex-1 px-6 py-6 max-w-3xl mx-auto w-full space-y-4">
        <div className="flex items-start gap-3 rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-700">
          <Info size={15} className="mt-0.5 shrink-0" />
          <span>{t('infoBanner')}</span>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <svg className="w-6 h-6 animate-spin text-orange-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8v4a4 4 0 0 0-4 4H4z" />
            </svg>
          </div>
        ) : shiftTypes.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <CalendarClock size={40} className="text-gray-300 mb-4" />
            <h2 className="text-base font-semibold text-gray-700 mb-1">{t('empty.title')}</h2>
            <p className="text-sm text-gray-500 mb-6 max-w-xs">{t('empty.description')}</p>
            <button
              onClick={openAdd}
              className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-orange-500 to-amber-600 px-4 py-2 text-sm font-semibold text-white shadow-md shadow-orange-200 transition hover:from-orange-600 hover:to-amber-700"
            >
              <Plus size={16} />
              {t('empty.cta')}
            </button>
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="px-5 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">{t('table.name')}</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">{t('table.timing')}</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">{t('table.duration')}</th>
                  <th className="px-5 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {shiftTypes.map((st) => {
                  const start = st.startTime || '';
                  const end = st.endTime || '';
                  const dur = start && end ? durationMins(start, end) : null;
                  const label = st.label || st.name || st.shiftName || '';
                  return (
                    <tr key={st.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-2.5">
                          <span
                            className="inline-block rounded-sm flex-shrink-0"
                            style={{
                              width: 14,
                              height: 14,
                              backgroundColor: st.colorHex || '#94A3B8',
                              border: '0.5px solid rgba(0,0,0,0.12)',
                            }}
                          />
                          <span className="font-medium text-gray-900">{label}</span>
                        </div>
                      </td>
                      <td className="px-5 py-3.5 text-gray-600">
                        {start && end ? `${formatTime12(start)} – ${formatTime12(end)}` : '—'}
                      </td>
                      <td className="px-5 py-3.5 text-gray-600">
                        {dur !== null ? formatDuration(dur) : '—'}
                      </td>
                      <td className="px-5 py-3.5 text-right">
                        <div className="flex items-center justify-end gap-3">
                          <button
                            onClick={() => openEdit(st)}
                            className="text-blue-600 hover:text-blue-700 font-medium text-xs"
                          >
                            {t('table.edit')}
                          </button>
                          <button
                            onClick={() => setDeletingShiftType(st)}
                            className="text-gray-400 hover:text-red-500 font-medium text-xs transition-colors"
                          >
                            {t('table.delete')}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ShiftTypeModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSaved={handleSaved}
        existing={editingShiftType}
      />

      <ConfirmDeleteShiftTypeModal
        open={!!deletingShiftType}
        shiftType={deletingShiftType}
        onClose={() => setDeletingShiftType(null)}
        onDeleted={handleDeleted}
      />
    </div>
  );
}

export default ShiftTypes;